import {usePanelSearchDebouncedContext} from '../../../state/panelbank/PanelSearchContextProvider';
import {isSectionPanelsAuto} from '../../../state/views/compress';
import * as PanelTypes from '../../../state/views/panel/types';
import {RunHistoryKeyInfo} from '../../../types/run';
import {usePersistentRumViewContext} from '../../../util/datadog';
import {panelIsActive, searchRegexMatchesPanel} from '../../../util/panelbank';
import {PANEL_BANK_HIDDEN_SECTION_NAME} from '../../../util/panelbankTypes';
import {useMemo} from '../../../util/profiler/react';
import {usePerformanceStatLogger} from '../../../util/profiler/react/usePerformanceStatLogger';
import {keyExistsInQueryString} from '../../../util/query-string';
import * as RunHelpers from '../../../util/runhelpers';
import {useWorkspaceSettingsContext} from '../../WorkspaceDrawer/Settings/WorkspaceSettingsContextProvider';
import {usePanelBankSectionsScrollContext} from '../PanelBankSectionsScrollContextProvider';
import type {PanelBankConfigWithRefs, SectionWithMetadata} from '../types';

export const usePanelBankSectionList = ({
  panelBankConfigWithRefs,
  isSingleRun,
  historyKeyInfo,
}: {
  panelBankConfigWithRefs: PanelBankConfigWithRefs;
  isSingleRun: boolean | undefined;
  historyKeyInfo: RunHistoryKeyInfo;
}) => {
  const keyTypes = RunHelpers.useKeyTypes(historyKeyInfo.keys);
  const {debouncedSearchRegex: searchRegex} = usePanelSearchDebouncedContext();
  const {workspaceLayout} = useWorkspaceSettingsContext();
  const {scrollToPanelBankSectionId} = usePanelBankSectionsScrollContext();

  // Filter out panels that don't have data, and panels that don't match the search query
  // Also adds SectionMetadata to the sections
  const numTotalSections = panelBankConfigWithRefs.sections.length;
  usePerformanceStatLogger(
    'workspace.sections_count',
    numTotalSections,
    v => v >= 1000
  );
  usePersistentRumViewContext('workspace.sections_count', numTotalSections);

  // QUESTION FOR FOLLOWING MEMO CALL:
  // do we really need to loop through every panel, before filtering sections?
  // do we need to loop through every section? or just the visible ones?
  // break the section+panel loops early when we have enough to display 1 page

  // Prettier ignore: avoid reformatting function needlessly when adding perf metrics:
  // prettier-ignore
  return useMemo(() => {
    const isSearching = !!searchRegex;

    return panelBankConfigWithRefs.sections.reduce(
      (filteredSections: SectionWithMetadata[], section, idx) => {
        // always hide last section "Hidden Panels"
        if (
          section.name === PANEL_BANK_HIDDEN_SECTION_NAME &&
          idx === panelBankConfigWithRefs.sections.length - 1 &&
          !keyExistsInQueryString('SHOW_HIDDEN_SECTION') // for dev debugging purposes
        ) {
          return filteredSections;
        }

        // activePanelRefs are the panels that may get rendered.
        // NOTE: this includes panels that match and don't match the panel search regex. we want to keep them in the same array so we can maintain the panel order
        // for flow sections, activePanelRefs = panels with data
        // for grid sections, activePanelRefs = all panels (we show placeholders for panels with no data)
        const activePanelRefs: PanelTypes.Ref[] = [];

        // inactivePanelRefs are panels with no data (or histograms in multi-run workspaces, see isPanelActive
        const inactivePanelRefs: PanelTypes.Ref[] = [];

        // panelSearchResults is a map of search results. keys are panelRefs, values are boolean
        const panelSearchResults = new Map<PanelTypes.Ref, boolean>();

        let panelSearchMatchCount = 0;
        section.panels.forEach(panel => {
          const isPanelActive = panelIsActive({
            section,
            panel,
            isSingleRun,
            historyKeyInfo,
            keyTypes,
          });

          // filter out panels that don't have data
          if (!isPanelActive) {
            inactivePanelRefs.push(panel.ref);
            return;
          }

          activePanelRefs.push(panel.ref);

          // If there's a searchRegex and the panel matches, set search result value to true
          if (
            searchRegex != null &&
            searchRegexMatchesPanel(searchRegex, panel)
          ) {
            panelSearchResults.set(panel.ref, true);
            panelSearchMatchCount++;
            return;
          }

          panelSearchResults.set(panel.ref, false);
          return;
        });

        // While searching, we also want to display sections with names that match the search
        const isSectionNameSearchMatch = searchRegex?.source.includes('?!')
          ? false
          : searchRegex?.test(section.name) ?? false;

        const hasActivePanels = activePanelRefs.length > 0;
        const isSectionVisible =
        // Force the virtualized section to render if the section is the one we want to scroll to. 
        // This is for the case when the user navigates to a full screen panel directly (not from the workspace) 
        // or refreshes the full screen panel page and then hits the back arrow
        scrollToPanelBankSectionId !== undefined && section.__id__ === scrollToPanelBankSectionId || 
          // showEmptySections is a user workspace setting that applies during search
          (isSearching && workspaceLayout.showEmptySections) ||
          // show section if not searching and it has active panels
          (!isSearching && hasActivePanels) ||
          // show section if searching and section name or any panels match the search
          (isSearching &&
            ((isSectionNameSearchMatch && hasActivePanels) ||
              panelSearchMatchCount > 0)) ||
          // Edge case: sections with no panels might be newly-created by the user.
          // We want to show these sections so panels can be dragged into them
          (activePanelRefs.length + inactivePanelRefs.length === 0 &&
            !isSectionPanelsAuto(section, workspaceLayout));

        if (!isSectionVisible) {
          return filteredSections;
        }

        filteredSections.push({
          ...section,
          activePanelRefs,
          inactivePanelRefs,
          panelSearchResults,
          panelSearchMatchCount,
          isSectionNameSearchMatch,
        });
        return filteredSections;
      },
      []
    );
  }, [
    searchRegex,
    historyKeyInfo,
    keyTypes,
    panelBankConfigWithRefs.sections,
    isSingleRun,
    workspaceLayout,
    scrollToPanelBankSectionId
  ], 'usePanelBankSectionList.filteredSections');
};
